/**
 * Editar a descrição do ticket depois de criado (pedido da PoolTrack, 03/10:
 * "Nesse campo de descrição, teria a possibilidade de editarmos após criação
 * manual do ticket?"). A lógica é PURA de propósito, sem React e sem alias
 * `@/`: o web não tem executor de testes, e a prova mora na API
 * (apps/api/src/shared/ticket-description-edit.test.ts), como a dos filtros
 * dos relatórios e a das respostas rápidas.
 *
 * A descrição é também a FICHA: o parser do @erp/shared (parseSgrDescription)
 * lê `Placa:`, `Nome:`, `Telefone:`… dela, e o disparo do WhatsApp tira o
 * telefone de lá — e, só no ticket aberto à mão no ERP, do solicitante quando a
 * ficha não tem (resolveDispatchPhone; no importado do TiFlux o solicitante é o
 * placeholder do worker, F-79). Desde a spec 031 a ficha tem campos próprios
 * (ticket-sheet-view), e o editor só avisa quando o disparo ficaria sem
 * telefone.
 */
import {
  dispatchPhoneSources,
  htmlToPlainText,
  resolveDispatchPhone,
  type DispatchPhoneInput,
  type StatusKind,
  type TicketDispatchStatus,
} from '@erp/shared';

/**
 * Tag HTML conhecida. A descrição que veio do TiFlux é HTML (`<p>…<br />…`); a
 * digitada no ERP é texto. Lista fechada de tags para que um texto com `<` e
 * `>` ("valor < 100 e > 50") não seja lido como HTML.
 */
const HTML_TAG =
  /<\/?(?:p|br|div|span|strong|b|i|em|u|s|a|ul|ol|li|h[1-6]|table|thead|tbody|tr|td|th|img|blockquote|pre|code|hr|font|sub|sup)\b[^<>]*>/i;

export function isHtmlDescription(description: string | null | undefined): boolean {
  return typeof description === 'string' && HTML_TAG.test(description);
}

/** Descrição em branco é ausência: '' e só espaços valem null. */
function present(description: string | null | undefined): string | null {
  return typeof description === 'string' && description.trim() !== '' ? description : null;
}

/**
 * O que o editor abre: texto como está; HTML vira texto, uma linha por `<br>`
 * ou parágrafo — é assim que o parser da ficha o lê, então a ficha continua
 * reconhecida depois de salva como texto.
 */
export function descriptionDraftOf(description: string | null | undefined): string {
  const value = present(description);
  if (value === null) return '';
  return isHtmlDescription(value) ? htmlToPlainText(value) : value;
}

/**
 * O corpo do PATCH, ou null quando nada mudou (fechar sem salvar, sem
 * requisição e sem linha no histórico).
 *
 * - quebra de linha do Windows vira `\n`; as pontas em branco saem; vazio
 *   limpa (`null`);
 * - abrir uma descrição HTML e salvar sem mexer NÃO a reescreve como texto: o
 *   rascunho igual ao que o editor abriu é "nada mudou".
 */
export function descriptionPatch(
  original: string | null | undefined,
  draft: string
): { description: string | null } | null {
  const next = draft.replace(/\r\n?/g, '\n').trim();
  const value = next === '' ? null : next;
  const before = present(original);
  if (before === null) return value === null ? null : { description: value };
  if (value !== null && (value === before || value === before.trim())) return null;
  if (value !== null && value === descriptionDraftOf(before)) return null;
  return { description: value };
}

/**
 * O ticket está na fila do disparo do WhatsApp e o disparo não acharia telefone
 * — o envio falharia com `phone_not_found`. É o MESMO resolveDispatchPhone do
 * envio (a descrição sempre; o solicitante só no ticket aberto à mão no ERP),
 * não uma cópia da regra: com a descrição do rascunho, o aviso some no instante
 * em que o envio passaria a achar o telefone.
 */
export function dispatchNeedsPhone(
  status: TicketDispatchStatus | null | undefined,
  ticket: DispatchPhoneInput
): boolean {
  return status === 'pending' && resolveDispatchPhone(ticket) === null;
}

/**
 * O aviso, dizendo ONDE o disparo procura — a mesma lista que o envio percorre
 * (dispatchPhoneSources). No ticket aberto à mão no ERP, o solicitante também.
 */
export function dispatchPhoneWarning(createdVia: string | null | undefined): string {
  // spec 031: o telefone mora na FICHA (a descrição ainda é lida, como camada)
  return dispatchPhoneSources(createdVia).includes('requestor')
    ? 'Na fila de disparo do WhatsApp, mas sem telefone válido na ficha, na descrição nem no solicitante: informe o telefone na ficha — sem ele, o disparo vai falhar.'
    : 'Na fila de disparo do WhatsApp, mas sem telefone válido na ficha nem na linha “Telefone:” da descrição: informe o telefone na ficha — sem ele, o disparo vai falhar.';
}

/**
 * Por que o lápis está desligado, ou null quando a descrição pode ser editada.
 * As mesmas recusas da API: espelhado do TiFlux é 409 `ticket_mirrored`;
 * fechado ou cancelado, 422 `ticket_not_open` (a descrição não é das
 * correções que valem em ticket fechado).
 */
export function descriptionEditBlock(ticket: {
  mirrored: boolean;
  status: { kind: StatusKind };
}): string | null {
  if (ticket.mirrored) return 'Ticket espelhado do TiFlux: a descrição se edita lá.';
  if (ticket.status.kind === 'closed') return 'Ticket fechado: reabra para editar a descrição.';
  if (ticket.status.kind === 'canceled') return 'Ticket cancelado: a descrição não muda mais.';
  return null;
}

// ── Histórico ───────────────────────────────────────────────────────────────

/**
 * O antes e o depois da descrição gravados no evento `updated` — null quando o
 * evento não os traz (os anteriores a este pedido só listavam o campo).
 */
export function descriptionChangeOf(
  payload: Record<string, unknown>
): { from: string | null; to: string | null } | null {
  const change = payload['description'];
  if (!change || typeof change !== 'object' || Array.isArray(change)) return null;
  const c = change as Record<string, unknown>;
  const side = (v: unknown) => (typeof v === 'string' ? v : null);
  if (!('from' in c) && !('to' in c)) return null;
  return { from: side(c['from']), to: side(c['to']) };
}

/** A descrição como texto, para ler: HTML vira texto, e vazio vira null. */
export function descriptionText(value: string | null): string | null {
  const v = present(value);
  if (v === null) return null;
  const text = isHtmlDescription(v) ? htmlToPlainText(v) : v;
  return text.trim() === '' ? null : text;
}
